import { Component, EventEmitter, Injector, Input, OnDestroy, OnInit, Output, QueryList, SimpleChanges, ViewChild, ViewChildren, ViewContainerRef } from '@angular/core';
import { BaseComponent } from 'src/app/common/base/base.component';
import { Observable, Subscription } from 'rxjs';
import { MatRow, MatTableDataSource } from '@angular/material/table';
import { FormStatusEnum, FormType } from 'src/app/common/enumerations/enumerations';
import { utils } from 'src/app/modules/libs/utils';
import { MatSort } from '@angular/material/sort';
import { RsfService, getResourceDisplayName } from '../rsf.service';
import { RSFUpdate } from '../store/rsf/rsf.action';
import { Kind, RSF } from '../rsf';
import { SafeHtml } from '@angular/platform-browser';

@Component({
  selector: 'rsf-side',
  templateUrl: './rsf-side.component.html',
  styleUrls: ['./rsf-side.component.scss']
})
export class RsfSideComponent extends BaseComponent implements OnInit, OnDestroy {

  @Input() filter?: string | null;
  @Output() rowSelected = new EventEmitter<any>();

  @ViewChild(MatSort) sort!: MatSort;

  @ViewChildren('matrow', { read: ViewContainerRef }) rows?: QueryList<ViewContainerRef>;
  @ViewChildren('matrow') datarows?: QueryList<MatRow>;

  rsf?: RSF | null;
  rsfs?: RSF[];
  rsfsFiltered?: RSF[];
  rsfs$!: Observable<RSF[]>;
  rsfsSubs!: Subscription;

  dataSource = new MatTableDataSource<RSF>();

  showClosed?: boolean;
  selectedRowIndex? = -1;

  displayedColumns: string[] = [
    'serialNo',
    'locationNames',
    'statusName',
  ];

  constructor(
    protected override injector: Injector,
    private service: RsfService
  ) {
    super(injector);
  }

  ngAfterViewInit(): void {
    this.setSort<RSF>(this.dataSource, this.sort);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['filter']) {
      this.applyFilter();
    }
  }

  override ngOnDestroy(): void {
    this.rsfsSubs?.unsubscribe();
    super.ngOnDestroy();
  }

  ngOnInit(): void {
    this.loadRSFs();
  }

  dataSourceCreated?: boolean;

  loadRSFs() {
    this.rsfs$ = this.store.select(state => state.RSFs.data);
    this.rsfsSubs = this.rsfs$.subscribe(async data => {
      if (!data.length) return;
      this.rsfs = data.filter(x => x.serialNo !== '');

      this.createDataSource();

      const openDocument = await this.checkOpenDocument<RSF>(FormType.RSF, data);
      if (openDocument) {
        this.showClosed = openDocument.closed ?? this.showClosed;
        if (openDocument.document.id != this.rsf?.id)
          this.getValues(openDocument.document);
      }
    });
  }

  /**
   * Rebuilds the side table while preserving the active search term after the RSF store refreshes.
   */
  createDataSource() {
    const currentFilter = this.filter?.trim().toLowerCase() ?? '';
    const filteredData = this.utils.cloneDeep(this.rsfs?.filter(x => ((x.status !== FormStatusEnum.Canceled && x.status !== FormStatusEnum.Closed) && !this.showClosed) || this.showClosed).sort((a, b) => utils.sort(a.serialNo, b.serialNo, false)));
    filteredData?.forEach(rsf => {
      const amendment = rsf.rsfAmendments?.find(x => x.active);
      if (!rsf.editingByID && amendment?.editingByID) {
        rsf.editingByID = amendment.editingByID;
        rsf.editingBy = amendment.editingBy;
      }
      // Distinguish an active Minor Amendment while preserving its current workflow status.
      if (rsf.formStatus)
        rsf.formStatus.name = this.formatStatusName(rsf.formStatus.name, amendment?.isMinorAmendment);
      rsf.amendmentCount = rsf.rsfAmendments?.filter(a => a.status > 0).length ?? 0;
      rsf.locationNames = rsf.rsfResourceRelations?.map(rr => `<span class="nowrap">${getResourceDisplayName(rr)}</span>`).join(', ');
      if (!!amendment)
        amendment.locationNames = amendment.rsfResourceRelations?.map(rr => `<span class="nowrap">${getResourceDisplayName(rr)}</span>`).join(', ');
    });
    this.dataSource = new MatTableDataSource(filteredData);
    // Reapply the predicate and filter because selecting an RSF replaces the table data source.
    this.createDataSourceSort();
    this.dataSource.filter = currentFilter;
  }

  /**
   * Formats the status for the compact side table and identifies active Minor Amendments.
   * @param status Status text returned by the RSF workflow.
   * @param isMinorAmendment Whether the active amendment uses the Minor Amendment process.
   * @returns HTML used by the side-table status cell.
   */
  formatStatusName(status: string, isMinorAmendment = false): string {
    if (status?.includes('/')) {
      const parts = status.split('/');
      const amend = parts[1].includes('Amendment');
      const amendmentIndicator = amend && isMinorAmendment ? '<span class="minor-amendment-indicator" title="Minor Amendment">Minor</span>' : '';
      status = `<span class="d-flex align-items-center justify-content-between"><span>${parts[0].trim()}</span>${amendmentIndicator}</span><span class="small d-flex align-items-center justify-content-between"><span>${parts[1].replaceAll('Amendment', '').trim()}</span><span class="material-icons medium">${amend ? 'healing' : ''}</span></span>`;
    } else if (isMinorAmendment && status?.includes('Amendment')) {
      status = status.replace('Amendment', 'Minor Amendment');
    }
    return status;
  }

  createDataSourceSort() {
    this.dataSource.filterPredicate = (data: RSF, filter: string) => {
      const term = filter.trim().toLowerCase();

      // Lookup requester name
      const requester = this.users.find(u => u.id === data.requesterID);
      const requesterName = requester?.name?.toLowerCase() ?? '';

      // Combine searchable fields
      const combined = [
        data.serialNo,
        data.formStatus?.name,
        requesterName,
        data.description,
      ].join(' ').toLowerCase();

      // Handle negative filter with "-" symbol
      if (term.startsWith('-')) {
        const negativeTerm = term.slice(1); // remove the "-"
        return !combined.includes(negativeTerm);
      }

      return combined.includes(term);
    };

    this.setSort<RSF>(this.dataSource, this.sort);
  }

  applyFilter() {
    this.createDataSource();
    this.dataSource.filter = this.filter?.trim().toLowerCase() ?? '';
  }

  rowUnselected() {
    this.rowSelected.emit(null);
    this.rsf = null;
    this.selectedRowIndex = -1;
  }

  async rowClicked(row: RSF) {
    if (await this.canDeactivate()) {
      if (this.rsf?.id == row.id) {
        this.rowUnselected();
      }
      else {
        this.getValues(row);
      }
    }
  }

  formatLocationNames(locationNames: string) {
    if (locationNames) {
      return utils.replaceAll(locationNames, ',', ', ');
    }
    return '';
  }

  async getValues(row: RSF) {
    this.selectedRowIndex = this.dataSource.data.findIndex(x => x.id === row.id);
    if (row?.id) {
      this.setOpenDocument(FormType.RSF, row.id);
      this.rsf = await this.getRSFByID(row.id);
      if (this.rsf && this.rsf?.id) {
        this.store.dispatch(new RSFUpdate(this.rsf?.id, this.rsf));
        this.selectedRowIndex = this.rsf?.id;
        this.rowSelected.emit(this.rsf);
        this.showElement(this.selectedRowIndex, 7);
      }
    }
  }

  getRSFByID(id: number): Promise<RSF> {
    return new Promise((resolve, reject) => {
      this.service?.getById(id).toPromise().then(rsf => {
        if (rsf) {
          rsf.retrievedByID = this.getCurrentUser()?.id;
          resolve(rsf);
        }
      });
    });
  }

  showElement(index: number, height: number) {
    const indexstr = index.toString();
    const row = this.rows?.find(
      (r) => r.element.nativeElement.getAttribute('position') === indexstr
    );
    if (row) {
      const rect = row.element.nativeElement.getBoundingClientRect();
      if (rect.y <= 0 || rect.y + rect.height > height) {
        row.element.nativeElement.scrollIntoView(false, {
          behavior: 'instant',
        });
      }
      return;
    }
  }

  getPosition(row: any): number {
    const i = this.dataSource.data.findIndex((r) => {
      return r.id === row.id;
    });
    return i;
  }


}
